import {
  AfterViewInit,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, catchError, filter, map, of, take, tap } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import { SpecialCharacters } from 'src/app/core/enums';
import { UserAdmin } from 'src/app/core/models/admin';
import { Items } from 'src/app/core/models/common/items.model';
import { TableColumn } from 'src/app/core/models/common/table-column.model';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { RolePermissionsUtils } from 'src/app/core/utils/role-permissions/role-permissions.utils';
import { ExitModalComponent } from '../../modal/exit-modal/exit-modal.component';
import { ProfileModalComponent } from '../../modal/profile-modal/profile-modal.component';
import {
  CloseType,
  FORM_STATUS,
} from '../../modal/profile-modal/profile-modal.constants';
import { BUTTON_CLICK } from '../../modal/warning-modal/warning-modal.constants';
import { ERROR_TEXT, IMAGE_SRC, STATUS_OPTIONS } from '../../shared-constants';
import {
  ColumnIds,
  ColumnText,
  CONSTS,
  EXCEL_FILE_NAME,
} from './user-admin-table.constants';

@Component({
  selector: 'app-user-admin-table',
  templateUrl: './user-admin-table.component.html',
  styleUrls: ['./user-admin-table.component.scss'],
  standalone: false,
})
export class UserAdminTableComponent implements OnInit, AfterViewInit {
  userService = inject(UserService);
  destroy = inject(DestroyRef);

  @Input() radioHeader!: string;
  @Input() firstOptionValue!: string;
  @Input() firstOptionName!: string;
  @Input() secondOptionValue!: string;
  @Input() secondOptionName!: string;
  @Input() headerText = CONSTS.WELCOME_TEXT;
  @Input() hasRadioButton = false;
  @Input() radioModel!: string;
  @Input() isAdminSearch = false;
  @Output() radioButtonChange = new EventEmitter<Event>();

  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  readonly displayedColumns = Object.values(ColumnIds);
  readonly COLUMNTEXT = ColumnText;
  readonly STATUS_OPTIONS = STATUS_OPTIONS;
  readonly IMAGE = IMAGE_SRC;
  readonly ERROR_TEXT = ERROR_TEXT;
  readonly SELECT_ALL = STATUS_OPTIONS.ALL_SELECTED;
  readonly CONSTS = CONSTS;
  readonly CHARS = SpecialCharacters;

  // Create column search objects
  createTableColumn(columnId: string): TableColumn {
    return {
      itemId: columnId,
      ids: [],
      formControl: new FormControl(''),
      checkAllIds: true,
    };
  }

  // Create admin table columns
  userIdData = this.createTableColumn(ColumnIds.USER_ID);
  employeeIdData = this.createTableColumn(ColumnIds.EMPLOYEE_ID);
  lastNameData = this.createTableColumn(ColumnIds.LAST_NAME);
  firstNameData = this.createTableColumn(ColumnIds.FIRST_NAME);
  locationGroupData = this.createTableColumn(ColumnIds.LOCATION_GROUP);
  locationData = this.createTableColumn(ColumnIds.LOCATION);
  userRoleData = this.createTableColumn(ColumnIds.USER_ROLE);
  emailData = this.createTableColumn(ColumnIds.EMAIL);
  statusData = this.createTableColumn(ColumnIds.STATUS);

  //Misc
  selectedUser!: UserAdmin;
  dataSource = new MatTableDataSource<UserAdmin>();
  userData: UserAdmin[] = [];
  currentUser?: UserAdmin;
  resetSelectDropdowns = new BehaviorSubject<boolean>(false);
  roles: Items[] = [];
  currRole = '';
  dropdownOpen = [false, false];
  sortLastUsedAsc = true;

  constructor(
    readonly dialog: MatDialog,
    public commonUtils: CommonUtils,
    private roleUtils: RolePermissionsUtils,
  ) {}

  /**
   * Lifecycle hook that initializes the component.
   *
   * - Retrieves the `UserService` instance from the service provider.
   * - Subscribes to the current user observable (`getCurrentUser$`).
   * - When a valid user is received:
   *    - Stores the user in `currentUser`.
   *    - Sets the current role.
   *    - Refreshes the data for the user's location.
   *    - Loads available roles.
   *
   * Uses `takeUntil(this.destroy$)` to prevent memory leaks on component destroy.
   */
  ngOnInit(): void {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((user): user is UserAdmin => !!user),
        tap((user) => {
          this.currentUser = user;
          this.currRole = this.currentUser?.roleName;
          this.refreshData(this.currentUser?.location);
          this.getRoles();
        }),
      )
      .subscribe();
  }

  /**
   * Toggles the loading state of the UI.
   *
   * @param flag - `true` to show loading modal and lock updates, `false` to hide it.
   *
   * Delegates to `commonUtils` for modal visibility and locking behavior.
   */
  toggleLoading(flag: boolean): void {
    this.commonUtils.updateLoadingModal(flag);
    this.commonUtils.lockUpdateLoadingModal(flag);
  }

  /**
   * Fetches all available user roles from the backend and populates the roles dropdown.
   *
   * - Calls `getAllRoles()` from `UserService`.
   * - Iterates through the roles and pushes them into the `roles` array.
   * - Marks each role as selected by default.
   *
   * Uses `takeUntil(this.destroy$)` to clean up subscriptions.
   */
  getRoles() {
    this.userService
      .getAllRoles()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((roles) => {
          // Add to User Role Dropdown
          roles.response.roles.forEach((role) => {
            this.roles.push({
              id: role.roleName,
              selected: true,
            });
          });
        }),
      )
      .subscribe();
  }

  /**
   * Lifecycle hook that runs after the component's view has been fully initialized.
   *
   * - Sets a custom filter predicate for the data source using `commonUtils.createFilter()`.
   * - This enables custom filtering logic for the table.
   */
  ngAfterViewInit(): void {
    this.dataSource.filterPredicate = this.commonUtils.createFilter();
  }

  /**
   * Exports the filtered data in the table to an Excel file.
   *
   * - Uses `commonUtils.exportExcel()` with:
   *    - Column headers (`ColumnText` enum values).
   *    - File name (`EXCEL_FILE_NAME` constant).
   *    - Filtered data from the table (`dataSource.filteredData`).
   */
  exportExcel(): void {
    this.commonUtils.exportExcel(
      Object.values(ColumnText),
      EXCEL_FILE_NAME,
      this.dataSource.filteredData,
    );
  }

  /**
   * Removes a user from the list and updates the backend.
   *
   * @param selectedUser - The user to remove from the list.
   *
   * - Calls `deleteUser()` from `UserService` using the user's login ID.
   * - On success:
   *    - Refreshes the data for the current user's location.
   *    - Filters out the deleted user from the data source.
   *    - Updates the success banner.
   *
   * Uses `takeUntil(this.destroy$)` to clean up subscriptions.
   */
  removeUserFromList(selectedUser: UserAdmin): void {
    const selectedId = selectedUser?.loginId || '';
    if (selectedId) {
      this.userService
        .deleteUser(selectedId)
        .pipe(take(1))
        .subscribe(() => {
          this.refreshData(this.currentUser?.location);
          const filteredData = this.dataSource.data.filter(
            (ele: UserAdmin) =>
              ele?.loginId?.toLowerCase() !=
              selectedUser?.loginId.toLowerCase(),
          );
          this.dataSource.data = filteredData;
          this.dataSource.sort = this.sort;
          this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
        });
    }
  }

  /**
   * Clears all table filters and resets the associated form controls and dropdowns.
   *
   * - Delegates to `commonUtils.clearAllFilters()` to reset:
   *   - The `MatTableDataSource` filters,
   *   - The local `userData` backing array,
   *   - The provided form controls for all filterable columns.
   * - Emits `true` on `resetSelectDropdowns` to instruct any select components to reinitialize.
   *
   * Side effects:
   * - Resets UI state for filters and dropdowns.
   */
  clearAllFilters(): void {
    this.commonUtils.clearAllFilters(this.dataSource, this.userData, [
      this.userIdData.formControl,
      this.employeeIdData.formControl,
      this.lastNameData.formControl,
      this.firstNameData.formControl,
      this.emailData.formControl,
      this.locationGroupData.formControl,
      this.locationData.formControl,
    ]);

    this.resetSelectDropdowns.next(true);

    this.sortLastUsedAsc = false;
  }

  /**
   * Applies the current set of filters to the table data source.
   *
   * - Uses `commonUtils.searchFilter()` with:
   *   - Field-level filter descriptors (`TableColumn`/wrapped configs) for text controls,
   *   - The table data source (`MatTableDataSource`),
   *   - Dropdown/status filters (e.g., user status and role).
   *
   * Side effects:
   * - Updates `dataSource.filteredData` according to the active filters.
   */
  search(): void {
    this.commonUtils.searchFilter(
      [
        this.userIdData,
        this.employeeIdData,
        this.lastNameData,
        this.firstNameData,
        this.locationGroupData,
        this.locationData,
        this.emailData,
      ],
      this.dataSource,
      [this.statusData, this.userRoleData],
    );
  }

  /**
   * Handles a status dropdown change for a given table field and reapplies filters.
   *
   * @param $event - The dropdown selection event payload (expected to be `Items`).
   * @param field  - The table column configuration for the status field being changed.
   *
   * Behavior:
   * - Writes the selected item into `field.ids[0]` to align with the filter structure.
   * - Calls `search()` to recompute the table filters with the updated status.
   */
  onStatusChangeForDropdown($event: unknown, field: TableColumn): void {
    field.ids[0] = $event as Items;
    this.search();
  }

  /**
   * Opens the Profile modal in ADD mode and handles its close result.
   *
   * @param user - Optional user data to prefill the add form; defaults to `null` if not provided.
   *
   * Behavior:
   * - Opens `ProfileModalComponent` with `FORM_STATUS.ADD`.
   * - Subscribes to `afterClosed()` (single emission via `take(1)`).
   * - Delegates the modal close payload to `handleProfileModalClose()` to cover:
   *   - SAVE → create user → refresh → success banner,
   *   - EXIT with changes → confirm → possibly reopen the modal.
   */
  addUser(user?: UserAdmin): void {
    this.dialog
      .open(ProfileModalComponent, {
        width: '47.5rem',
        disableClose: true,
        data: {
          data: user ?? null,
          formStatus: FORM_STATUS.ADD,
          disableEmailRole: true,
          modalHeader: CONSTS.ADD_USER_HEADER,
          isOwnProfile: false,
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((model: CloseType) => this.handleProfileModalClose(model));
  }

  /**
   * Opens the Profile modal in VIEW mode for a given user.
   *
   * @param user - The user to view. A deep copy is used to avoid mutating the original object.
   *
   * Behavior:
   * - Clones the user object (`JSON.parse(JSON.stringify(user))`) to prevent side effects,
   * - Opens `ProfileModalComponent` with `FORM_STATUS.VIEW`,
   * - Disables action button(s) (`showButton: false`) for a pure read-only experience.
   */
  viewUser(user: UserAdmin): void {
    const _userSnapshot = JSON.parse(JSON.stringify(user));
    this.dialog.open(ProfileModalComponent, {
      width: '47.5rem',
      disableClose: true,
      data: {
        data: _userSnapshot,
        formStatus: FORM_STATUS.VIEW,
        disableEmailRole: true,
        showButton: false,
        modalHeader: CONSTS.VIEW_USER_HEADER,
        isOwnProfile: false,
      },
    });
  }

  /**
   * Opens the Profile modal in EDIT mode, handles save and exit flows.
   *
   * @param user       - The user to edit. A deep copy is passed to the modal to avoid side effects.
   * @param isReOpened - Flag indicating this modal instance was reopened after an exit warning (default `false`).
   *
   * Behavior:
   * - Opens `ProfileModalComponent` with `FORM_STATUS.EDIT`.
   * - On SAVE (`onExit === false`):
   *   - Calls `UserService.editUser(model.data)`, then refreshes data and shows success banner.
   * - On EXIT with changes:
   *   - Shows `ExitModalComponent`; if exit is rejected, recursively calls `editUser` with `isReOpened = true`.
   *
   * Notes:
   * - Uses `takeUntil(this.destroy$)` to avoid leaks on teardown.
   * - Includes basic error handling via `catchError(() => of())` to keep the stream alive.
   */
  editUser(user: UserAdmin, isReOpened = false): void {
    // flatten user for edit
    if (user) {
      const _userSnapshot = JSON.parse(JSON.stringify(user));
      const dialogRef = this.dialog.open(ProfileModalComponent, {
        width: '47.5rem',
        disableClose: true,
        data: {
          data: _userSnapshot,
          formStatus: FORM_STATUS.EDIT,
          disableEmailRole: true,
          modalHeader: CONSTS.EDIT_USER_HEADER,
          isReOpened,
          isOwnProfile: false,
        },
      });
      dialogRef.afterClosed().subscribe((model: CloseType) => {
        if (model.onExit === false) {
          this.userService
            .editUser(model.data)
            .pipe(
              take(1),
              catchError(() => {
                return of();
              }),
            )
            .subscribe(() => {
              this.refreshData(this.currentUser?.location);
              this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
            });
        } else {
          if (!model.untouched) {
            this.dialog?.afterAllClosed?.pipe(take(1)).subscribe(() => {
              this.dialog
                .open(ExitModalComponent, {
                  width: '24.375rem',
                  disableClose: true,
                  autoFocus: false,
                  data: {
                    ignoreExit: true,
                  },
                })
                .afterClosed()
                .pipe(take(1))
                .subscribe((button) => {
                  if (button === BUTTON_CLICK.REJECT) {
                    this.editUser(model.data, true);
                  }
                });
            });
          }
        }
      });
    }
  }

  /**
   * Opens a warning modal to confirm user removal, and removes the user if accepted.
   *
   * @param admin - The user/admin to remove.
   *
   * Behavior:
   * - Displays a confirmation modal with user details,
   * - On ACCEPT, calls `removeUserFromList(admin)` which:
   *   - Invokes backend deletion,
   *   - Refreshes table data and updates UI feedback (banner).
   */
  removeUser(admin: UserAdmin): void {
    this.commonUtils
      .openWarningModal({
        header: this.CONSTS.REMOVE_USER_HEADER,
        body: this.CONSTS.REMOVE_USER_TEXT,
        extraText: `${admin?.givenName} ${admin?.familyName}`,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((resp) => {
        if (resp.button === BUTTON_CLICK.ACCEPT) {
          this.removeUserFromList(admin);
        }
      });
  }

  /**
   * Loads all users for a given location (or all if undefined) and binds them to the table.
   *
   * @param locationCode - Optional location code to scope the user list.
   *
   * Behavior:
   * - Retrieves `UserService` and calls `getAllUsers(locationCode)`.
   * - On response:
   *   - Sorts users by `familyName` (ascending),
   *   - Populates `userData` and binds sorted array to `dataSource.data`.
   *
   * Notes:
   * - Uses `takeUntil(this.destroy$)` for stream cleanup on destroy.
   * - Defensive checks for response shape to guard against null/undefined.
   */
  getAllUsers(locationCode?: string): void {
    this.userService
      .getAllUsers(locationCode)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((bundled) => {
        const users = bundled?.response?.users;
        if (users && users.length > 0) {
          users.sort((a, b) =>
            (a.familyName?.toString() || '').localeCompare(
              b.familyName?.toString() || '',
            ),
          );
          users.forEach((data) => {
            this.userData.push(data);
          });
        }
        this.dataSource.data = users;

        this.sortData(ColumnIds.LAST_LOGIN, false);
      });
  }

  /**
   * Emits the radio button change event to parent components/consumers.
   *
   * @param $event - The original radio button change event.
   *
   * Behavior:
   * - Forwards the event through `radioButtonChange` `EventEmitter`.
   * - Useful for reacting to row-level or toolbar radio changes outside this component.
   */
  emitRadioButtonChange($event: Event): void {
    this.radioButtonChange.emit($event);
  }

  sortData(column: string, flipValue = true) {
    if (flipValue) {
      if (column === ColumnIds.LAST_LOGIN) {
        this.sortLastUsedAsc = !this.sortLastUsedAsc;
      }
    }

    this.dataSource.data.sort((a: UserAdmin, b: UserAdmin) => {
      switch (column) {
        case ColumnIds.LAST_LOGIN:
          return this.convertData(
            a.lastLoggedIn as string,
            b.lastLoggedIn as string,
            this.sortLastUsedAsc,
          );
        default:
          return 0;
      }
    });
    this.dataSource._updateChangeSubscription();
  }

  /**
   * Indicates whether the current role is allowed to add users.
   *
   * Delegates to `roleUtils.canAddUsers(currRole)` which encapsulates
   * permission logic for the active role.
   *
   * @returns `true` if the current role can add users; otherwise `false`.
   */
  get canAddUsers(): boolean {
    return this.roleUtils.canAddUsers(this.currRole);
  }

  /**
   * Indicates whether the current role is allowed to edit users.
   *
   * Behavior:
   * - When `isAdminSearch` is `true`, uses `roleUtils.canEditAdminSearchUsers(currRole)`,
   *   which applies the permissions specific to the Admin Search context.
   * - Otherwise, uses `roleUtils.canEditDeleteUserAdminUsers(currRole)`,
   *   which applies the permissions for the User Admin context.
   *
   * @returns `true` if the current role can edit users in the active context; otherwise `false`.
   */
  get canEditUsers(): boolean {
    return this.isAdminSearch
      ? this.roleUtils.canEditAdminSearchUsers(this.currRole)
      : this.roleUtils.canEditDeleteUserAdminUsers(this.currRole);
  }

  /**
   * Indicates whether the current role is allowed to delete users.
   *
   * Behavior:
   * - When `isAdminSearch` is `true`, uses `roleUtils.canDeleteAdminSearchUsers(currRole)`,
   *   which applies the permissions specific to the Admin Search context.
   * - Otherwise, uses `roleUtils.canEditDeleteUserAdminUsers(currRole)`,
   *   which governs delete/edit permissions for the User Admin context.
   *
   * @returns `true` if the current role can delete users in the active context; otherwise `false`.
   */
  get canDeleteUsers(): boolean {
    return this.isAdminSearch
      ? this.roleUtils.canDeleteAdminSearchUsers(this.currRole)
      : this.roleUtils.canEditDeleteUserAdminUsers(this.currRole);
  }

  /**
   * Indicates whether any filter is currently active/enabled.
   *
   * Checks text inputs for non-empty values and dropdown filters for selected IDs.
   * This is commonly used to enable/disable the "Clear Filters" button or to show
   * active filter badges.
   *
   * Evaluated fields:
   * - Text controls: userId, employeeId, lastName, firstName, location, locationGroup, email.
   * - Dropdowns: userRole (`ids.length > 0`), status (`ids.length > 0`).
   *
   * @returns `true` if at least one filter is applied; otherwise `false`.
   */
  get isFilterEnabled(): boolean {
    return (
      this.userIdData.formControl.value !== '' ||
      this.employeeIdData.formControl.value !== '' ||
      this.lastNameData.formControl.value !== '' ||
      this.firstNameData.formControl.value !== '' ||
      this.locationData.formControl.value !== '' ||
      this.locationGroupData.formControl.value !== '' ||
      this.emailData.formControl.value !== '' ||
      this.userRoleData.ids.length > 0 ||
      this.statusData.ids.length > 0
    );
  }

  /**
   * Refreshes the table data and resets all filter state.
   *
   * Steps:
   * 1. Clears all filters and resets dropdowns (`clearAllFilters()`).
   * 2. Resets local data caches (`userData`) and all column `ids` arrays.
   * 3. Reloads users:
   *    - If `isAdminSearch` is `true`, loads **all users** across locations.
   *    - Otherwise, loads users **scoped to the provided `locationCode`**.
   *
   * @param locationCode - Optional location code used when not in Admin Search mode.
   *
   * Side effects:
   * - Mutates filter state (`ids`, form controls).
   * - Triggers data fetch via `getAllUsers(...)`.
   * - Updates `dataSource.data` indirectly through `getAllUsers`.
   */
  private refreshData(locationCode?: string): void {
    this.clearAllFilters();
    this.userData = [];
    this.userIdData.ids = [];
    this.employeeIdData.ids = [];
    this.lastNameData.ids = [];
    this.firstNameData.ids = [];
    this.emailData.ids = [];
    this.locationData.ids = [];
    this.locationGroupData.ids = [];
    this.userRoleData.ids = [];

    // get all users from every group if true
    // otherwise get by current selected location
    this.isAdminSearch ? this.getAllUsers() : this.getAllUsers(locationCode);
  }

  /**
   * Handles the close result of the Profile modal across all open/reopen flows.
   *
   * @param model - The payload emitted by `ProfileModalComponent` on close.
   *                Expected shape: `{ data, onExit, untouched? }`.
   *
   * Behavior:
   * - SAVE path (`onExit === false`):
   *   - Obtains `UserService` from the provider.
   *   - Calls `createUser(model.data)` and, on success:
   *     - Refreshes data for the current location,
   *     - Shows API success banner.
   *
   * Notes:
   * - Ensures the reopened modal is also handled (important when `take(1)` completes on the first close).
   * - Uses `takeUntil(this.destroy$)` to prevent leaks on component teardown.
   */
  private handleProfileModalClose(model: CloseType): void {
    if (!model) return;

    // SAVE path
    if (model.onExit === false) {
      this.userService
        .createUser(model.data)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.refreshData(this.currentUser?.location);
            this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
          },
        });
      return;
    }
  }

  private convertData(a: string, b: string, sortAsc: boolean) {
    const aVal = a ? a.toString().toLowerCase() : '';
    const bVal = b ? b.toString().toLowerCase() : '';
    if (sortAsc) {
      return bVal.localeCompare(aVal);
    } else {
      return aVal.localeCompare(bVal);
    }
  }
}
